import { memo, useId, useState } from "react";
import { clampQty, editQty, settleQty } from "@helm/ui";
import { Icon } from "./Icon";
import { QtyFavorites } from "./QtyFavorites";

/**
 * No client-side cap by default: HELM imposes no contract limit of its own (owner 2026-09-28,
 * ADR-0033, replacing SPEC §9.6's "else 10"). The account's own limits are shown beside the
 * quantity (`QtyLimitBadge`) and enforced where they live: the user's per-account max by the
 * gateway, the account's RMS limit by Rithmic.
 */
export const UNBOUNDED_QTY = Number.MAX_SAFE_INTEGER;

/**
 * Quantity stepper, optionally with the user's favourite quantity chips and their 1–10 list (DOM
 * mode; `QtyFavorites`). Choosing a quantity is local UI state, not a trading action, so it stays
 * enabled. Scroll wheel over the field steps it (SPEC §9.6).
 */
export const QtyStepper = memo(function QtyStepper({
  value,
  onChange,
  favorites = false,
  max = UNBOUNDED_QTY,
  label = "Quantity",
  compact = false,
}: {
  value: number;
  onChange: (q: number) => void;
  /** Show the favourite quantity chips and the "▾" 1–10 list (synced settings, `QtyFavorites`). */
  favorites?: boolean;
  max?: number;
  label?: string;
  compact?: boolean;
}): React.JSX.Element {
  const id = useId();
  // The text being typed (`null` when not editing). It may be empty: an empty field keeps the
  // previous quantity and is restored on blur / Enter, never 1 (OB4: backspace then "3" gave 13).
  const [draft, setDraft] = useState<string | null>(null);
  const set = (q: number): void => {
    setDraft(null);
    onChange(clampQty(q, max));
  };
  const settle = (): void => {
    if (draft === null) return;
    setDraft(null);
    const q = settleQty(draft, value, max);
    if (q !== value) onChange(q);
  };
  return (
    <div className={compact ? "qty qty-compact" : "qty"}>
      <div className="qty-stepper" role="group" aria-labelledby={`${id}-l`}>
        <span id={`${id}-l`} className="sr-only">
          {label}
        </span>
        <button
          type="button"
          className="qty-btn"
          aria-label="Decrease quantity"
          onClick={() => {
            set(value - 1);
          }}
          disabled={value <= 1}
        >
          <Icon name="minus" size={12} />
        </button>
        <input
          className="qty-input num"
          inputMode="numeric"
          aria-label={label}
          value={draft ?? String(value)}
          onChange={(e) => {
            const edit = editQty(e.target.value, max);
            setDraft(edit.draft);
            if (edit.commit !== null && edit.commit !== value) onChange(edit.commit);
          }}
          onBlur={settle}
          onWheel={(e) => {
            if (document.activeElement !== e.currentTarget) return;
            set(value + (e.deltaY < 0 ? 1 : -1));
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") {
              e.preventDefault();
              set(value + 1);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              set(value - 1);
            } else if (e.key === "Enter") {
              settle();
            }
          }}
        />
        <button
          type="button"
          className="qty-btn"
          aria-label="Increase quantity"
          onClick={() => {
            set(value + 1);
          }}
          disabled={value >= max}
        >
          <Icon name="plus" size={12} />
        </button>
      </div>
      {favorites && <QtyFavorites value={value} onPick={set} max={max} />}
    </div>
  );
});
